import { CustomerIdentifier } from './customer_identifier.js';
import { FulfillmentMethod } from './fulfillment_method.js';
import { allEnumValues } from './helper.js';
import * as OrderChannelHelper from './order_channel_helper.js';
import { OrderChannel } from './order_channel.js';

export function name(fulfillmentMethod: FulfillmentMethod): string {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
      return 'To Go';
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
      return 'Dine In';
    case FulfillmentMethod.KIOSK_OUTPOST:
      return 'Outpost Pickup';
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.WEB_TO_GO:
      return 'Pickup';
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.WEB_DINE_IN:
      return 'Dine In';
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.WEB_CURBSIDE:
      return 'Curbside';
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.WEB_OUTPOST:
      return 'Outpost Pickup';
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.WEB_DELIVERY:
      return 'Delivery';
  }
}

function getFulfillmentMethodOfOrderChannel(
  channel: OrderChannel,
  isFulfillmentMethodOfCategory: (fulfillmentMethod: FulfillmentMethod) => boolean,
): FulfillmentMethod | null {
  return (
    allEnumValues<FulfillmentMethod>(FulfillmentMethod).find((fulfillmentMethod) => {
      return (
        orderChannel(fulfillmentMethod) === channel &&
        isFulfillmentMethodOfCategory(fulfillmentMethod)
      );
    }) ?? null
  );
}

export function getToGo(orderChannel: OrderChannel): FulfillmentMethod | null {
  return getFulfillmentMethodOfOrderChannel(orderChannel, isToGo);
}

export function getDineIn(orderChannel: OrderChannel): FulfillmentMethod | null {
  return getFulfillmentMethodOfOrderChannel(orderChannel, isDineIn);
}

export function getCurbside(orderChannel: OrderChannel): FulfillmentMethod | null {
  return getFulfillmentMethodOfOrderChannel(orderChannel, isCurbside);
}

export function getOutpost(orderChannel: OrderChannel): FulfillmentMethod | null {
  return getFulfillmentMethodOfOrderChannel(orderChannel, isAnOutpost);
}

export function getDelivery(orderChannel: OrderChannel): FulfillmentMethod | null {
  return getFulfillmentMethodOfOrderChannel(orderChannel, isDelivery);
}

export function nameWithChannel(fulfillmentMethod: FulfillmentMethod): string {
  const diningOptionName = name(fulfillmentMethod);
  const orderChannelEnum = orderChannel(fulfillmentMethod);
  return `${diningOptionName} (${OrderChannelHelper.name(orderChannelEnum)})`;
}

export function orderChannel(fulfillmentMethod: FulfillmentMethod): OrderChannel {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
      return OrderChannel.DriveThru;
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.KIOSK_TO_GO:
      return OrderChannel.Kiosk;
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
      return OrderChannel.Linebuster;
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.FLASH_TO_GO:
      return OrderChannel.Flash;
    case FulfillmentMethod.WEB_CURBSIDE:
    case FulfillmentMethod.WEB_DELIVERY:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_OUTPOST:
    case FulfillmentMethod.WEB_TO_GO:
      return OrderChannel.Web;
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.CATERING_TO_GO:
      return OrderChannel.Catering;
  }
}

export function isKiosk(fulfillmentMethod: FulfillmentMethod): boolean {
  return OrderChannelHelper.isKiosk(orderChannel(fulfillmentMethod));
}

export function isToGo(fulfillmentMethod: FulfillmentMethod): boolean {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_TO_GO:
      return true;
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.WEB_CURBSIDE:
    case FulfillmentMethod.WEB_DELIVERY:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_OUTPOST:
      return false;
  }
}

export function isDineIn(fulfillmentMethod: FulfillmentMethod): boolean {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.WEB_DINE_IN:
      return true;
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_CURBSIDE:
    case FulfillmentMethod.WEB_DELIVERY:
    case FulfillmentMethod.WEB_OUTPOST:
    case FulfillmentMethod.WEB_TO_GO:
      return false;
  }
}

export function isAnOutpost(fulfillmentMethod: FulfillmentMethod): boolean {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.WEB_OUTPOST:
      return true;
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_CURBSIDE:
    case FulfillmentMethod.WEB_DELIVERY:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_TO_GO:
      return false;
  }
}

export function isCurbside(fulfillmentMethod: FulfillmentMethod): boolean {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.WEB_CURBSIDE:
      return true;
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_DELIVERY:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_OUTPOST:
    case FulfillmentMethod.WEB_TO_GO:
      return false;
  }
}

export function isDelivery(fulfillmentMethod: FulfillmentMethod): boolean {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.WEB_DELIVERY:
      return true;
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_CURBSIDE:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_OUTPOST:
    case FulfillmentMethod.WEB_TO_GO:
      return false;
  }
}

export function customerIdentifiers(fulfillmentMethod: FulfillmentMethod): CustomerIdentifier[] {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_DINE_IN:
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
    case FulfillmentMethod.FLASH_DINE_IN:
    case FulfillmentMethod.FLASH_TO_GO:
    case FulfillmentMethod.KIOSK_DINE_IN:
    case FulfillmentMethod.KIOSK_TO_GO:
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
    case FulfillmentMethod.LINEBUSTER_TO_GO:
    case FulfillmentMethod.WEB_DINE_IN:
    case FulfillmentMethod.WEB_TO_GO:
      return [
        CustomerIdentifier.BiteOrderNumber,
        CustomerIdentifier.GuestEmail,
        CustomerIdentifier.GuestName,
        CustomerIdentifier.PhoneNumber,
        CustomerIdentifier.PosOrderNumber,
        CustomerIdentifier.TableNumber,
      ];
    case FulfillmentMethod.CATERING_OUTPOST:
    case FulfillmentMethod.CATERING_TO_GO:
    case FulfillmentMethod.FLASH_OUTPOST:
    case FulfillmentMethod.KIOSK_OUTPOST:
    case FulfillmentMethod.WEB_OUTPOST:
      return [
        CustomerIdentifier.BiteOrderNumber,
        CustomerIdentifier.GuestEmail,
        CustomerIdentifier.GuestName,
        CustomerIdentifier.PhoneNumber,
        CustomerIdentifier.PosOrderNumber,
      ];
    case FulfillmentMethod.CATERING_CURBSIDE:
    case FulfillmentMethod.FLASH_CURBSIDE:
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
    case FulfillmentMethod.WEB_CURBSIDE:
      return [
        CustomerIdentifier.BiteOrderNumber,
        CustomerIdentifier.GuestEmail,
        CustomerIdentifier.GuestName,
        CustomerIdentifier.PhoneNumber,
        CustomerIdentifier.PosOrderNumber,
        CustomerIdentifier.VehicleDescription,
      ];
    case FulfillmentMethod.CATERING_DELIVERY:
    case FulfillmentMethod.FLASH_DELIVERY:
    case FulfillmentMethod.WEB_DELIVERY:
      return [
        CustomerIdentifier.Address,
        CustomerIdentifier.BiteOrderNumber,
        CustomerIdentifier.GuestEmail,
        CustomerIdentifier.GuestName,
        CustomerIdentifier.PhoneNumber,
        CustomerIdentifier.PosOrderNumber,
      ];
  }
}

export type FulfillmentMethodString =
  | 'catering-curbside'
  | 'catering-delivery'
  | 'catering-dine-in'
  | 'catering-outpost'
  | 'catering-to-go'
  | 'drive-thru-to-go'
  | 'flash-curbside'
  | 'flash-delivery'
  | 'flash-dine-in'
  | 'flash-outpost'
  | 'flash-to-go'
  | 'kiosk-dine-in'
  | 'kiosk-outpost'
  | 'kiosk-to-go'
  | 'linebuster-curbside'
  | 'linebuster-dine-in'
  | 'linebuster-to-go'
  | 'web-curbside'
  | 'web-delivery'
  | 'web-dine-in'
  | 'web-outpost'
  | 'web-to-go';

export function stringFromEnum(fulfillmentMethod: FulfillmentMethod): FulfillmentMethodString {
  switch (fulfillmentMethod) {
    case FulfillmentMethod.CATERING_CURBSIDE:
      return 'catering-curbside';
    case FulfillmentMethod.CATERING_DELIVERY:
      return 'catering-delivery';
    case FulfillmentMethod.CATERING_DINE_IN:
      return 'catering-dine-in';
    case FulfillmentMethod.CATERING_OUTPOST:
      return 'catering-outpost';
    case FulfillmentMethod.CATERING_TO_GO:
      return 'catering-to-go';
    case FulfillmentMethod.DRIVE_THRU_TO_GO:
      return 'drive-thru-to-go';
    case FulfillmentMethod.FLASH_CURBSIDE:
      return 'flash-curbside';
    case FulfillmentMethod.FLASH_DELIVERY:
      return 'flash-delivery';
    case FulfillmentMethod.FLASH_DINE_IN:
      return 'flash-dine-in';
    case FulfillmentMethod.FLASH_OUTPOST:
      return 'flash-outpost';
    case FulfillmentMethod.FLASH_TO_GO:
      return 'flash-to-go';
    case FulfillmentMethod.KIOSK_DINE_IN:
      return 'kiosk-dine-in';
    case FulfillmentMethod.KIOSK_OUTPOST:
      return 'kiosk-outpost';
    case FulfillmentMethod.KIOSK_TO_GO:
      return 'kiosk-to-go';
    case FulfillmentMethod.LINEBUSTER_CURBSIDE:
      return 'linebuster-curbside';
    case FulfillmentMethod.LINEBUSTER_DINE_IN:
      return 'linebuster-dine-in';
    case FulfillmentMethod.LINEBUSTER_TO_GO:
      return 'linebuster-to-go';
    case FulfillmentMethod.WEB_CURBSIDE:
      return 'web-curbside';
    case FulfillmentMethod.WEB_DELIVERY:
      return 'web-delivery';
    case FulfillmentMethod.WEB_DINE_IN:
      return 'web-dine-in';
    case FulfillmentMethod.WEB_OUTPOST:
      return 'web-outpost';
    case FulfillmentMethod.WEB_TO_GO:
      return 'web-to-go';
  }
}

export function enumFromString(
  fulfillmentMethodString: FulfillmentMethodString,
): FulfillmentMethod {
  switch (fulfillmentMethodString) {
    case 'catering-curbside':
      return FulfillmentMethod.CATERING_CURBSIDE;
    case 'catering-delivery':
      return FulfillmentMethod.CATERING_DELIVERY;
    case 'catering-dine-in':
      return FulfillmentMethod.CATERING_DINE_IN;
    case 'catering-outpost':
      return FulfillmentMethod.CATERING_OUTPOST;
    case 'catering-to-go':
      return FulfillmentMethod.CATERING_TO_GO;
    case 'drive-thru-to-go':
      return FulfillmentMethod.DRIVE_THRU_TO_GO;
    case 'flash-curbside':
      return FulfillmentMethod.FLASH_CURBSIDE;
    case 'flash-delivery':
      return FulfillmentMethod.FLASH_DELIVERY;
    case 'flash-dine-in':
      return FulfillmentMethod.FLASH_DINE_IN;
    case 'flash-outpost':
      return FulfillmentMethod.FLASH_OUTPOST;
    case 'flash-to-go':
      return FulfillmentMethod.FLASH_TO_GO;
    case 'kiosk-dine-in':
      return FulfillmentMethod.KIOSK_DINE_IN;
    case 'kiosk-outpost':
      return FulfillmentMethod.KIOSK_OUTPOST;
    case 'kiosk-to-go':
      return FulfillmentMethod.KIOSK_TO_GO;
    case 'linebuster-curbside':
      return FulfillmentMethod.LINEBUSTER_CURBSIDE;
    case 'linebuster-dine-in':
      return FulfillmentMethod.LINEBUSTER_DINE_IN;
    case 'linebuster-to-go':
      return FulfillmentMethod.LINEBUSTER_TO_GO;
    case 'web-curbside':
      return FulfillmentMethod.WEB_CURBSIDE;
    case 'web-delivery':
      return FulfillmentMethod.WEB_DELIVERY;
    case 'web-dine-in':
      return FulfillmentMethod.WEB_DINE_IN;
    case 'web-outpost':
      return FulfillmentMethod.WEB_OUTPOST;
    case 'web-to-go':
      return FulfillmentMethod.WEB_TO_GO;
  }
}

export function getOutpostFulfillmentMethods(): FulfillmentMethod[] {
  return allEnumValues<OrderChannel>(OrderChannel)
    .map(getOutpost)
    .filter((x) => x !== null);
}
